<template>
  <div class="card p-2.5">
    <div class="flex flex-wrap justify-between items-start gap-3 mb-3">
      <div class="flex flex-col gap-2">
        <SelectButton :modelValue="entity" :options="entityOptions" optionLabel="label" optionValue="value"
          :allowEmpty="false" size="small" aria-label="Record type" data-testid="custom-fields-entity"
          @update:modelValue="setEntity" />
        <Tabs v-if="isStudents && canViewRequests" :value="view" @update:value="setView">
          <TabList>
            <Tab value="fields" data-testid="student-fields-tab">Fields</Tab>
            <Tab value="requests" data-testid="data-requests-tab">Requests</Tab>
          </TabList>
        </Tabs>
        <h2 class="text-lg font-bold">{{ showRequests ? 'Data requests' : entityCopy.heading }}</h2>
        <p class="text-sm text-surface-500 max-w-2xl">
          {{ showRequests ? 'Ask students to fill in details on their own profile by a date, and see who has.' : entityCopy.description }}
        </p>
      </div>
      <div v-if="showRequests" class="flex flex-wrap gap-2">
        <Button v-if="canSend" label="Ask students for details" icon="pi pi-plus" size="small" @click="ask()"
          data-testid="new-data-request" />
      </div>
      <div v-else-if="canCreate" class="flex flex-wrap gap-2">
        <Button v-if="templates.length" label="Add from template" icon="pi pi-clone" size="small" outlined
          @click="templatesVisible = true" data-testid="btn-custom-field-templates" />
        <Button label="New field" icon="pi pi-plus" size="small" @click="openCreate"
          data-testid="btn-custom-field-new" />
      </div>
    </div>

    <StudentDataRequestsList v-if="showRequests" />

    <StudentFieldsTable v-else-if="isStudents" :canUpdate="canUpdate" :canDelete="canDelete" :canSend="canSend"
      :reordering="reordering" @move="move" @edit="openEdit" @delete="confirmDelete" @ask="(key: string) => ask([key])" />

    <DataTable v-else :value="fields" :loading="loading" size="small" dataKey="id" data-testid="custom-fields-table">
      <template #empty>
        <div class="py-6 text-center text-surface-500" data-testid="custom-fields-empty">
          No custom fields yet.<span v-if="canCreate"> Use "New field" to add the first one<template
            v-if="templates.length">, or "Add from template" for a ready-made set</template>.</span>
        </div>
      </template>
      <Column header="Order" style="width: 6rem">
        <template #body="{ data, index }">
          <div class="flex">
            <Button icon="pi pi-arrow-up" text rounded size="small" aria-label="Move up"
              :disabled="!canUpdate || index === 0 || reordering" @click="move(data.id, -1)"
              :data-testid="`btn-custom-field-up-${data.key}`" />
            <Button icon="pi pi-arrow-down" text rounded size="small" aria-label="Move down"
              :disabled="!canUpdate || index === fields.length - 1 || reordering" @click="move(data.id, 1)"
              :data-testid="`btn-custom-field-down-${data.key}`" />
          </div>
        </template>
      </Column>
      <Column header="Field">
        <template #body="{ data }">
          <div class="flex flex-col">
            <span class="font-medium">{{ data.label }}</span>
            <code class="text-xs text-surface-500">{{ data.key }}</code>
          </div>
        </template>
      </Column>
      <Column header="Type">
        <template #body="{ data }">
          {{ customFieldTypeLabel(data.type) }}
          <span v-if="customFieldChoicesSummary(data, fields)" class="text-xs text-surface-500"
            :data-testid="`custom-field-choices-${data.key}`">({{ customFieldChoicesSummary(data, fields) }})</span>
        </template>
      </Column>
      <Column header="Required">
        <template #body="{ data }">
          <i :class="data.is_required ? 'pi pi-check text-primary' : 'pi pi-minus text-surface-400'" />
        </template>
      </Column>
      <Column header="Status">
        <template #body="{ data }">
          <Tag :value="data.is_active ? 'Active' : 'Retired'" :severity="data.is_active ? 'success' : 'secondary'" />
        </template>
      </Column>
      <Column :header="`${entityCopy.label} with a value`" field="values_count" />
      <Column header="" style="width: 7rem">
        <template #body="{ data }">
          <Button v-if="canUpdate" icon="pi pi-pencil" text rounded size="small" aria-label="Edit"
            @click="openEdit(data)" :data-testid="`btn-custom-field-edit-${data.key}`" />
          <Button v-if="canDelete" icon="pi pi-trash" severity="danger" text rounded size="small" aria-label="Delete"
            @click="confirmDelete(data)" :data-testid="`btn-custom-field-delete-${data.key}`" />
        </template>
      </Column>
    </DataTable>

    <CustomFieldFormDialog v-model:visible="dialogVisible" :field="editing" :entity="entity" :fields="fields" @saved="refetch()" />
    <CustomFieldTemplatesDialog v-model:visible="templatesVisible" :entity="entity" @applied="refetch()" />
    <StudentDataRequestWizard v-if="wizardFields" :initialFields="wizardFields" @close="wizardFields = null" @sent="onSent" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { CustomFieldEntity } from '@/types/composition-functions';
import type { CustomFieldFieldsFragment } from '@/types/operations';
import { useUserStore } from '@/store/userStore';
import { AppPermissions, can } from '@/utils/permissions';
import { useCustomFields } from '@/composables/queries/useCustomFields';
import { useCustomFieldMutations } from '@/composables/mutations/useCustomFieldMutations';
import { useCustomFieldTemplates } from '@/composables/queries/useCustomFieldTemplates';
import {
  CUSTOM_FIELD_ENTITY_OPTIONS, customFieldChoicesSummary, customFieldEntityCopy, customFieldEntityFromSlug,
  customFieldTypeLabel, moveFieldOrder,
} from '@/utils/customFields';
import type { DataRequest } from '@/utils/studentDataRequests';
import CustomFieldFormDialog from './CustomFieldFormDialog.vue';
import CustomFieldTemplatesDialog from './CustomFieldTemplatesDialog.vue';
import StudentFieldsTable from './StudentFieldsTable.vue';
import StudentDataRequestsList from '@/components/DashBoard/StudentDataRequests/StudentDataRequestsList.vue';
import StudentDataRequestWizard from '@/components/DashBoard/StudentDataRequests/StudentDataRequestWizard.vue';

/**
 * Settings → Custom Fields. Students get two tabs: Fields (built-in and
 * custom fields in one table) and Requests (asking students to fill them
 * in). ?entity=students|staff|guardians and ?view=fields|requests open one
 * directly.
 */
const userStore = useUserStore();
const toast = useToast();
const confirm = useConfirm();
const route = useRoute();
const router = useRouter();

const permission = (action: string) => can(userStore.currentUser, action, AppPermissions.CustomFields.Resource);
const canCreate = computed(() => permission(AppPermissions.CustomFields.Actions.Create));
const canUpdate = computed(() => permission(AppPermissions.CustomFields.Actions.Update));
const canDelete = computed(() => permission(AppPermissions.CustomFields.Actions.Delete));
const canViewRequests = computed(() => can(userStore.currentUser, AppPermissions.Students.Actions.View, AppPermissions.Students.Resource));
const canSend = computed(() => can(userStore.currentUser, AppPermissions.Students.Actions.Update, AppPermissions.Students.Resource));

const entity = computed(() => customFieldEntityFromSlug(route.query.entity));
const entityOptions = [...CUSTOM_FIELD_ENTITY_OPTIONS];
const entityCopy = computed(() => customFieldEntityCopy(entity.value));
const isStudents = computed(() => entity.value === CustomFieldEntity.Student);
const view = computed(() => (route.query.view === 'requests' ? 'requests' : 'fields'));
const showRequests = computed(() => isStudents.value && canViewRequests.value && view.value === 'requests');

function setEntity(value: CustomFieldEntity) {
  router.replace({ query: { ...route.query, entity: customFieldEntityCopy(value).slug, view: undefined } });
}

function setView(value: string | number) {
  router.replace({ query: { ...route.query, view: String(value) } });
}

/** The wizard's starting fields while it is open: a field's "Ask students", or none. */
const wizardFields = ref<string[] | null>(null);
function ask(keys: string[] = []) {
  wizardFields.value = keys;
}

function onSent(request: DataRequest) {
  wizardFields.value = null;
  router.push({ name: 'admin-student-data-request', params: { id: request.id } });
}

const { fields, loading, refetch } = useCustomFields({ entity, includeInactive: true });
const { templates } = useCustomFieldTemplates({ entity, enabled: canCreate });
const templatesVisible = ref(false);
const { reorder, remove } = useCustomFieldMutations();

const dialogVisible = ref(false);
const editing = ref<CustomFieldFieldsFragment | null>(null);
const reordering = ref(false);

function openCreate() {
  editing.value = null;
  dialogVisible.value = true;
}

function openEdit(field: CustomFieldFieldsFragment) {
  editing.value = field;
  dialogVisible.value = true;
}

async function move(id: string, direction: -1 | 1) {
  reordering.value = true;
  try {
    await reorder.mutate({ entity: entity.value, ids: moveFieldOrder(fields.value.map(f => f.id), id, direction) });
    await refetch();
  } catch (error) {
    console.error(error);
    toast.add({ severity: 'error', summary: 'Could not reorder', detail: (error as Error)?.message || 'Please try again.', life: 5000 });
  } finally {
    reordering.value = false;
  }
}

function confirmDelete(field: CustomFieldFieldsFragment) {
  const impact = field.values_count > 0
    ? ` ${field.values_count} ${entityCopy.value.noun}(s) have a value for it, and those values will be deleted too. To keep them, retire the field instead (edit it and turn Active off) — retired fields are hidden but keep their values.`
    : '';
  confirm.require({
    header: 'Delete custom field?',
    message: `Delete "${field.label}"?${impact}`,
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: 'Cancel', severity: 'secondary', text: true },
    acceptProps: { label: 'Delete', severity: 'danger' },
    accept: async() => {
      try {
        await remove.mutate({ id: field.id });
        toast.add({ severity: 'success', summary: 'Deleted', detail: `"${field.label}" was deleted.`, life: 3000 });
        await refetch();
      } catch (error) {
        console.error(error);
        toast.add({ severity: 'error', summary: 'Could not delete', detail: (error as Error)?.message || 'Please try again.', life: 5000 });
      }
    },
  });
}
</script>
